import moment from 'moment-timezone';
import CoachingConstants from 'coaching/CoachingConstants';
import { CoachAvailabilityInterval, DayOfWeek } from 'globalTypes';
import { uniqWith } from 'lodash';
import { dateOnlyFormat } from 'lib/dateTimeFormat';

const all24Hours = [...Array(25).keys()];
const addLeadingZero = (num: number) => String(num).padStart(2, '0');
const timeSelectOptions = all24Hours.map(hour => {
  const value = `${addLeadingZero(hour)}:00`;
  const label = moment(value, 'HH:mm').format('h:mm A');
  return { label, value };
});

const firstTimeSelectOption = timeSelectOptions[0];
const lastTimeSelectOption = timeSelectOptions[timeSelectOptions.length - 1];

function parseMidnightEndTime(interval: CoachAvailabilityInterval) {
  return {
    startTime: interval.startTime,
    endTime: interval.endTime === '00:00' ? '24:00' : interval.endTime,
  };
}

function getTimeSelectOptionIndexForTime(time?: string) {
  const option = timeSelectOptions.find(option => option.value === time) ?? firstTimeSelectOption;
  return timeSelectOptions.indexOf(option);
}

function findValidTimeSelectOptions(interval: CoachAvailabilityInterval) {
  const startTimeOptionIndex = getTimeSelectOptionIndexForTime(interval.startTime);
  const endTimeOptionIndex = getTimeSelectOptionIndexForTime(interval.endTime);
  const validStartTimeOptions = timeSelectOptions.slice(0, endTimeOptionIndex);
  const validEndTimeOptions = timeSelectOptions.slice(startTimeOptionIndex + 1);
  return { validStartTimeOptions, validEndTimeOptions };
}

function consolidateTimeIntervals(
  intervals: CoachAvailabilityInterval[]
): CoachAvailabilityInterval[] {
  // generate an array of number arrays that represent
  // the indices of the start and end times within the
  // timeSelectOption array
  // ie. { startTime: "15:00", endTime: "16:00" } -> [3, 4]
  const intervalIndicesArray = intervals.map(interval => [
    getTimeSelectOptionIndexForTime(interval.startTime),
    getTimeSelectOptionIndexForTime(interval.endTime),
  ]);

  // the callback function to use in the eventual array
  // reduction for determining overlap between intervals
  const mergeIntervalIndices = (intervalIndicesArray: number[][], intervalIndices: number[]) => {
    const lastIndex = intervalIndicesArray.length - 1;

    if (
      intervalIndicesArray.length === 0 ||
      intervalIndicesArray[lastIndex][1] < intervalIndices[0]
    ) {
      // if no intervals are in the array yet, OR the startTime of
      // the new interval is greater than the endTime of the last
      // interval, push the indices to the array
      intervalIndicesArray.push(intervalIndices);
    } else {
      // otherwise, modify the last interval in the array
      // using the new interval's values instead of pushing
      // the new interval to the array
      intervalIndicesArray[lastIndex][1] = Math.max(
        intervalIndicesArray[lastIndex][1],
        intervalIndices[1]
      );
    }
    return intervalIndicesArray;
  };

  // sort and reduce the array of interval indices
  const mergedIntervalIndicesArray = intervalIndicesArray
    .sort((a, b) => a[0] - b[0])
    .reduce(mergeIntervalIndices, []);

  // convert back into `CoachAvailabilityInterval`s
  return mergedIntervalIndicesArray.map(i => ({
    startTime: timeSelectOptions[i[0]].value,
    endTime: timeSelectOptions[i[1]].value,
  }));
}

function getWeekdayForWeekOffset(weekday: number, weekOffset: number) {
  return moment().day(weekday + weekOffset * 7);
}

function getWeeklyTimeframe(weekOffset: number) {
  const startDateTime = getWeekdayForWeekOffset(0, weekOffset).hour(0);
  const endDateTime = getWeekdayForWeekOffset(6, weekOffset).hour(23);
  return { startDateTime, endDateTime };
}

function getWeeklyTimeframeString(weekOffset: number) {
  const { startDateTime, endDateTime } = getWeeklyTimeframe(weekOffset);
  const monday = startDateTime.format('MMM D');
  const thursday = endDateTime.format('MMM D');

  return `${monday} - ${thursday}`;
}

function getWeeklyTimeframeISOStrings(weekOffset: number) {
  const { startDateTime, endDateTime } = getWeeklyTimeframe(weekOffset);

  return { startDateTime: startDateTime.toISOString(), endDateTime: endDateTime.toISOString() };
}

function getWeeklyTimeframeDateOnlyStrings(weekOffset: number, timezone: string) {
  const { startDateTime, endDateTime } = getWeeklyTimeframe(weekOffset);

  return {
    startDate: dateOnlyFormat(startDateTime.toISOString(), timezone),
    endDate: dateOnlyFormat(endDateTime.toISOString(), timezone),
  };
}

function calculateTotalHoursFromIntervals(intervals: CoachAvailabilityInterval[]) {
  const hoursArray = intervals.map(interval => {
    const startTimeIndex = getTimeSelectOptionIndexForTime(interval?.startTime);
    const endTimeIndex = getTimeSelectOptionIndexForTime(interval?.endTime);
    return endTimeIndex - startTimeIndex;
  });

  return hoursArray.reduce((a, b) => a + b, 0);
}

function getDayOfWeekIndex(dayOfWeek: DayOfWeek) {
  return CoachingConstants.SORTED_DAYS_OF_WEEK.indexOf(dayOfWeek);
}

function getNextNCoachingDays(
  n: number,
  weekOffset: number,
  userTimezone: string,
  startDate?: string
) {
  const numberArray = Array.from(Array(n * 3), (_, i) => i + 1);
  // add a day so that we don't return the current day
  const weekOffsetToDays = 1 + 7 * weekOffset;
  const firstDay = moment.tz(startDate, userTimezone).add(weekOffsetToDays, 'days');
  const nextNTimes3Days = numberArray.reduce(
    (acc, curr) => [
      ...acc,
      // set all dates to the very end of the day
      moment
        .tz(startDate, userTimezone)
        .add(curr + weekOffsetToDays, 'days')
        .set('hours', 23)
        .set('minutes', 59)
        .set('seconds', 59),
    ],
    [firstDay]
  );
  const nextNCoachingDays = nextNTimes3Days.filter(d => d.day() >= 1 && d.day() <= 4).slice(0, n);
  // keep the first day 24 hours ahead of the current time/startDate so that
  // coaches aren't scheduled less than 24 hours in advance
  return [firstDay, ...nextNCoachingDays.slice(1)];
}

/*
 * Given a list of reservation timestamps that may have slots for the same day,
 * this returns a list of unique dates the reservations are on.
 */
export function getDatesFromReservations(reservations: moment.Moment[]): moment.Moment[] {
  const reservationsToShow = reservations.map(x =>
    x
      .clone()
      .set('hours', 23)
      .set('minutes', 59)
      .set('seconds', 59)
  );
  return uniqWith(reservationsToShow, (x, y) => x.isSame(y, 'day'));
}

export {
  all24Hours,
  getDayOfWeekIndex,
  getWeekdayForWeekOffset,
  parseMidnightEndTime,
  timeSelectOptions,
  findValidTimeSelectOptions,
  consolidateTimeIntervals,
  firstTimeSelectOption,
  lastTimeSelectOption,
  getWeeklyTimeframe,
  getWeeklyTimeframeString,
  getWeeklyTimeframeISOStrings,
  getWeeklyTimeframeDateOnlyStrings,
  calculateTotalHoursFromIntervals,
  getNextNCoachingDays,
};
